
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import type { Node, Edge } from '@xyflow/react'
import type { AINodeType, AiModelPurpose } from '@genieartinc/schemas'
import type {
  CanvasNodeData,
  FullNodeState,
} from '@/types/canvas'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { FEATURES } from '@/lib/features'
import { isNodeControlFamily } from '@/components/canvas/editor/node-settings/node-control-help'

/**
 * Component types whose feature flag is off. Nothing can add one any more, but a
 * canvas saved before the flag went off still holds them: React Flow no longer
 * has the type registered, so the node falls back to a plain box — selectable,
 * and with no Generate button. The settings panel has to follow it. Opening
 * model settings for such a node fetches its model library, auto-selects the
 * first model and saves that back onto the node (useNodeSettingsState), which
 * dirties the canvas with a model for a job that can never be submitted.
 *
 * The type mappings below stay intact, so the panel works again the moment the
 * flag flips back on.
 */
const GATED_COMPONENT_TYPES: readonly string[] = FEATURES.NODE_3D ? [] : ['ThreeDNode']

function isGatedComponentType(componentType: string | undefined): boolean {
  return componentType !== undefined && GATED_COMPONENT_TYPES.includes(componentType)
}

function helpOnlySelection(node: Node, nodeData: CanvasNodeData | undefined): SelectedNodeInfo {
  const nodeState: Partial<FullNodeState> = nodeData?.state || {}
  return {
    id: node.id,
    componentType: node.type || 'unknown',
    title: nodeState.title || nodeData?.title || 'Node',
    purpose: 'create',
  }
}

interface UseCanvasNodeSettingsParams {
  nodes: Node[]
  edges: Edge[]
  selectedNodes: string[]
}

interface SelectedNodeInfo {
  id: string
  /** Omitted for file and utility nodes, which have no model. */
  type?: AINodeType
  componentType: string
  title: string
  purpose: AiModelPurpose
  subPurpose?: string
  initialParams?: {
    modelFamilySlug?: string
    modelId?: string
    settings: Record<string, unknown>
    systemPrompt?: string
    pinnedParameters?: string[]
  }
}

interface UseCanvasNodeSettingsReturn {
  nodeSettingsVisible: boolean
  nodeSettingsCollapsed: boolean
  setNodeSettingsCollapsed: React.Dispatch<React.SetStateAction<boolean>>
  selectedNodeData: SelectedNodeInfo | null
  handleNodeSettingsCollapse: () => void
  hasSelectedNode: boolean
}

/**
 * Manages node settings panel visibility and selected node data computation.
 *
 * Responsibilities:
 * - Determine when to show/hide node settings panel based on selection
 * - Compute selectedNodeData with parent/child relationship handling
 * - Handle settings panel collapse/expand state
 */
export function useCanvasNodeSettings({
  nodes,
  edges,
  selectedNodes,
}: UseCanvasNodeSettingsParams): UseCanvasNodeSettingsReturn {
  const [nodeSettingsVisible, setNodeSettingsVisible] = useState(false)
  const [nodeSettingsCollapsed, setNodeSettingsCollapsed] = useState(false)

  // Monitor selected nodes and show/hide node settings
  const selectedNodeId = selectedNodes[0]
  const selectedNodesCount = selectedNodes.length
  // Track selected node's component type so visibility re-evaluates on swap (TextNode ↔ TextFile)
  const selectedNodeComponentType = selectedNodeId
    ? nodes.find(n => n.id === selectedNodeId)?.type
    : undefined

  useEffect(() => {
    // Show settings only if exactly 1 node is selected and it's a supported type.
    // A generated image still opens the panel: either the parent's controls, or the file's own help.
    if (selectedNodesCount === 1 && selectedNodeId && selectedNodeComponentType && !isGatedComponentType(selectedNodeComponentType) && isNodeControlFamily(selectedNodeComponentType)) {
      if (selectedNodeComponentType === 'ImageFile') {
        const selectedNode = nodes.find(n => n.id === selectedNodeId)
        const nodeData = selectedNode?.data as unknown as CanvasNodeData | undefined
        const parentNodeId = selectedNode?.parentId || nodeData?.component?.state?.parentNodeId
        const parentType = parentNodeId ? nodes.find(n => n.id === parentNodeId)?.type : undefined
        const hasParentEdge = Boolean(parentNodeId) && edges.some(edge =>
          edge.source === parentNodeId &&
          edge.target === selectedNodeId &&
          edge.targetHandle === 'parent-input'
        )
        const snapshot = nodeData?.component?.state?.generationSnapshot
        if (hasParentEdge && !isGatedComponentType(parentType) && snapshot) {
          queueMicrotask(() => {
            const settingsApi = useCanvasBridgeStore.getState().settingsApi
            if (settingsApi && typeof settingsApi.loadSnapshot === 'function') {
              settingsApi.loadSnapshot({
                modelId: snapshot.modelId,
                modelFamilySlug: snapshot.modelFamilySlug ?? snapshot.brandFamily,
                settings: snapshot.settings,
              })
            }
          })
        }
      }
      setNodeSettingsVisible(true)
    } else {
      setNodeSettingsVisible(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, selectedNodesCount, selectedNodeComponentType])

  // Performance: Extract node data only when selection changes (not on every render)
  const firstSelectedNodeId = selectedNodes[0]
  const selectedCount = selectedNodes.length

  // Stabilize selectedNodeData: only return a new reference when relevant fields change
  // This prevents the nodeSettingsPanel useMemo from recreating JSX on every nodes change
  const prevSelectedNodeDataRef = useRef<SelectedNodeInfo | null>(null)

  const selectedNodeData = useMemo(() => {
    let next: SelectedNodeInfo | null = null

    if (nodeSettingsVisible && selectedCount === 1) {
      const selectedNode = nodes.find(n => n.id === firstSelectedNodeId)
      if (selectedNode) {
        const nodeData = selectedNode.data as unknown as CanvasNodeData
        const nodeType = selectedNode.type

        if (isGatedComponentType(nodeType)) {
          // Left over from a switched-off feature: nothing to drive a model with
          next = null
        } else if (nodeType === 'ImageFile') {
          // ImageFile: Show parent's settings if parent exists
          const parentNodeId = selectedNode.parentId || nodeData?.state?.parentNodeId
          if (parentNodeId) {
            const hasParentEdge = edges.some(edge =>
              edge.source === parentNodeId &&
              edge.target === selectedNode.id &&
              edge.targetHandle === 'parent-input'
            )
            if (hasParentEdge) {
              const parentNode = nodes.find(n => n.id === parentNodeId)
              if (parentNode && !isGatedComponentType(parentNode.type)) {
                const parentData = parentNode.data as unknown as CanvasNodeData
                const parentState: Partial<FullNodeState> = parentData?.state || {}
                const parentTypeMap: Record<string, string> = { TextNode: 'text-llm', TextFile: 'text-llm', ImageNode: 'image', VideoNode: 'video', ThreeDNode: '3d' }
                const parentAiNodeType = parentTypeMap[parentNode.type || ''] || 'text-llm'
                next = {
                  id: parentNode.id,
                  type: parentAiNodeType as AINodeType,
                  componentType: parentNode.type as string,
                  title: parentState.title || parentData?.title || 'Parent Node',
                  purpose: (parentState.purpose || 'create') as AiModelPurpose,
                  subPurpose: parentState.subPurpose as string | undefined,
                  initialParams: {
                    modelFamilySlug: parentState.modelFamilySlug ?? parentState.brandFamily,
                    modelId: parentState.modelId,
                    settings: parentState.settings || {},
                    systemPrompt: parentState.systemPrompt,
                    pinnedParameters: parentState.pinnedParameters as string[] | undefined,
                  }
                }
              }
            }
          }
          // No runnable parent: explain the file itself. Never fall through to a gated parent's model.
          if (!next) next = helpOnlySelection(selectedNode, nodeData)
        } else if (nodeType === 'TextNode' || nodeType === 'TextFile' || nodeType === 'ImageNode' || nodeType === 'VideoNode' || nodeType === 'ThreeDNode' || nodeType === 'ShaderNode') {
          const nodeTypeMap: Record<string, string> = { TextNode: 'text-llm', TextFile: 'text-llm', ImageNode: 'image', VideoNode: 'video', ThreeDNode: '3d', ShaderNode: 'shader' }
          const aiNodeType = nodeTypeMap[nodeType] || 'text-llm'
          const nodeState: Partial<FullNodeState> = nodeData?.state || {}
          next = {
            id: selectedNode.id,
            type: aiNodeType as AINodeType,
            componentType: nodeType,
            title: nodeState.title || nodeData?.title || 'Node',
            purpose: (nodeState.purpose || 'create') as AiModelPurpose,
            subPurpose: nodeState.subPurpose as string | undefined,
            initialParams: nodeState.modelId ? {
              modelFamilySlug: nodeState.modelFamilySlug ?? nodeState.brandFamily,
              modelId: nodeState.modelId,
              settings: nodeState.settings || {},
              systemPrompt: nodeState.systemPrompt,
              pinnedParameters: nodeState.pinnedParameters as string[] | undefined,
            } : undefined,
          }
        } else if (nodeType && isNodeControlFamily(nodeType)) {
          next = helpOnlySelection(selectedNode, nodeData)
        }
      }
    }

    // Stabilize: return previous reference if data is equivalent
    const prev = prevSelectedNodeDataRef.current
    if (prev === null && next === null) return null
    if (prev && next &&
      prev.id === next.id &&
      prev.type === next.type &&
      prev.componentType === next.componentType &&
      prev.title === next.title &&
      prev.purpose === next.purpose &&
      prev.subPurpose === next.subPurpose &&
      prev.initialParams?.modelId === next.initialParams?.modelId &&
      prev.initialParams?.modelFamilySlug === next.initialParams?.modelFamilySlug &&
      prev.initialParams?.systemPrompt === next.initialParams?.systemPrompt &&
      JSON.stringify(prev.initialParams?.settings) === JSON.stringify(next.initialParams?.settings)
    ) {
      return prev // Same reference — no downstream re-render
    }

    prevSelectedNodeDataRef.current = next
    return next
  }, [
    nodeSettingsVisible,
    firstSelectedNodeId,
    selectedCount,
    nodes,
    edges,
  ])

  // Handle node settings collapse/expand
  const handleNodeSettingsCollapse = useCallback(() => {
    setNodeSettingsCollapsed(prev => !prev)
  }, [])

  return {
    nodeSettingsVisible,
    nodeSettingsCollapsed,
    setNodeSettingsCollapsed,
    selectedNodeData,
    handleNodeSettingsCollapse,
    hasSelectedNode: nodeSettingsVisible,
  }
}
